import React, { useCallback, useState } from 'react';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { ExtendedTheme, TextField, UserInterfaceSearch, Button } from 'neon-design-system';
import SectionHeader from '../../ui/SectionHeader';
import ArticlePreviewList from '../../ui/ArticlePreviewList';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import ExploreBySector from './ExploreBySector';

interface SearchForm {
	searchValue: string;
}

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	searchInput: {
		marginBottom: theme.spacing(2)
	},
	sectorCardList: {
		marginBottom: theme.spacing(4)
	},
	icon: {
		color: theme.palette.grey[200]
	},
	upperSection: {
		padding: `${theme.spacing(1)}px 0`,
		marginBottom: theme.spacing(4)
	},
	hidden: {
		display: 'none'
	}
}));

const formErrorMessage = 'Please type at least 2 characters';

const SearchPage: React.FC = () => {
	const classes = useStyles();

	const [sendText, setSendText] = useState<string>('');
	const [articlesCount, setArticlesCount] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	useActivityLogger('Search', 'Search');

	const initialValues: SearchForm = {
		searchValue: ''
	};

	const { handleSubmit, handleBlur, handleChange, values, errors, touched } = useFormik<SearchForm>({
		initialValues,
		validationSchema: Yup.object({
			searchValue: Yup.string()
				.min(2, formErrorMessage)
		}),
		onSubmit: ({ searchValue }: SearchForm) => {
			if (sendText === searchValue) {
				return;
			}
			setSendText(searchValue);
			if (!searchValue) {
				return;
			}
			setLoading(true);
		}
	});

	const onDataLoadedHandler = useCallback((count: number) => {
		setLoading(false);
		setArticlesCount(count);
	}, []);

	const articleCaption =
		articlesCount === 0 ?
			`No articles for '${sendText}'` :
			`${articlesCount} ${articlesCount > 1 ? 'articles' : 'article'} found for '${sendText}'`;
	const articleSubcaption = articlesCount === 0 ? 'No articles were found based on your search query' : 'Your search results';

	return (
		<form
			onSubmit={handleSubmit}
			autoComplete="off">
			<Grid container spacing={2}>
				<Grid
					className={classes.upperSection}
					item
					xs={12}
					lg={4}
					sm={6}
					md={6}>
					<SectionHeader
						title="Search and explore"
						subtitle="Search within all articles in the Neon Insights platform"
						id="search-header"
					/>
					<div className={classes.searchInput}>
						<TextField
							className={classes.searchText}
							prefix={<UserInterfaceSearch className={classes.icon}/>}
							size="large"
							name="searchValue"
							value={values.searchValue}
							error={!!(errors.searchValue && touched.searchValue)}
							placeholder="Search in article name or content"
							errorText={errors.searchValue}
							onBlur={handleBlur}
							onChange={handleChange}/>
					</div>
					<div>
						<Button
							type="submit"
							className={classes.enableButton}
							size="medium"
							busy={loading}
							colorType="primary">
							Search articles
						</Button>
					</div>
				</Grid>
				<Grid
					className={classes.hidden}
					item
					xs={12}>
					<ExploreBySector className={classes.sectorCardList} />
				</Grid>
				{!!sendText && <Grid
					className={loading ? classes.hidden : ''}
					item
					xs={12}>
					<SectionHeader title={articleCaption} subtitle={articleSubcaption} id="result-header"/>
					<div className={sendText && articlesCount > 0 ? '' : classes.hidden}>
						<ArticlePreviewList
							source="SearchArticle"
							onDataLoaded={onDataLoadedHandler}
							searchQuery={sendText}/>
					</div>
				</Grid>}
			</Grid>
		</form>
	);
};

export default SearchPage;
